import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ISelectOption } from "components/Select";
import { cn } from "utils/cn";
import { AGENT_CARD, SLEEK_CONTROL } from "pages/DesignCanvasAgentPage/components/sleek";

/**
 * The controls' two sizes (2026-10-26): `md` the Trends pages' row (48 px), `sm` every other page's toolbar (36 px) —
 * the same finish, smaller.
 */
export type SleekSize = "sm" | "md";
export const SLEEK_FIELD: Record<SleekSize, string> = {
  md: "h-12 gap-2.5 pl-5 pr-4 text-[15px]",
  sm: "h-9 gap-2 pl-3.5 pr-3 text-[13px]",
};

export interface SleekMenuOption {
  id: string;
  title: string;
  /** A line under it, saying what it is ("Previous season"). */
  hint?: string;
  disabled?: boolean;
}

/**
 * The redesign's dropdown, one for every picker: the analytics cards' frame (`AGENT_CARD`: a hairline edge, a faint inner
 * glow) with a soft, short shadow — not the chat menus' heavy one — and the picked row held by a quiet ground, as the
 * story's step index holds its step. `search` sits on top when the list is long.
 */
export const SleekMenu = ({
  options,
  value,
  values,
  onPick,
  search,
  empty,
  className,
}: {
  options: SleekMenuOption[];
  value: string;
  /** Several picked (a multi-select): each marked with a tick, the menu a list to toggle. */
  values?: string[];
  onPick: (id: string) => void;
  search?: React.ReactNode;
  /** Said in place of the list when it holds nothing (no match for the search, nothing under the filters). */
  empty?: string;
  className?: string;
}) => (
  <div
    role="listbox"
    className={cn(
      AGENT_CARD,
      "absolute left-0 top-full z-30 mt-2 flex max-h-80 flex-col rounded-2xl p-1.5 shadow-[inset_0_0_8px_rgba(0,0,0,0.04),0_12px_28px_-16px_rgba(0,0,0,0.22)] dark:shadow-[inset_0_0_8px_rgba(255,255,255,0.06),0_12px_28px_-16px_rgba(0,0,0,0.6)]",
      className
    )}
  >
    {search}
    <div className="min-h-0 overflow-y-auto">
      {!options.length && empty && <p className="px-3 py-2.5 text-[13px] leading-snug text-grey">{empty}</p>}
      {options.map((option) => {
        const selected = values ? values.includes(option.id) : option.id === value;

        return (
          <button
            key={option.id}
            type="button"
            role="option"
            aria-selected={selected}
            disabled={option.disabled}
            onClick={() => onPick(option.id)}
            className={cn(
              "flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-[14px] font-normal leading-tight text-trueBlack transition-colors hover:bg-body-150 disabled:cursor-not-allowed disabled:opacity-40 dark:text-white",
              selected && !values && "bg-body-150 font-medium"
            )}
          >
            {values && (
              // A multi-select's rows are checkboxes: ticked on ink, empty on a hairline.
              <span
                aria-hidden
                className={cn(
                  "flex h-4 w-4 flex-none items-center justify-center rounded-[4px] border transition-colors",
                  selected ? "border-trueBlack bg-trueBlack text-white dark:border-white dark:bg-white dark:text-trueBlack" : "border-black-300 bg-body"
                )}
              >
                {selected && (
                  <svg viewBox="0 0 16 16" className="h-3 w-3">
                    <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
            )}
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate">{option.title}</span>
              {option.hint && <span className="mt-0.5 text-[12px] text-grey">{option.hint}</span>}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

/** A filter's empty list: nothing matches what was typed, or nothing is left under the search and the other filters. */
// eslint-disable-next-line i18next/no-literal-string -- a prototype screen; its copy moves to the locales with the screen.
const emptyOf = (query: string) => (query ? `Nothing matches “${query}”.` : "Nothing to choose under this search and these filters.");

/** An open menu shuts on a press outside its box, or on Escape. */
const useDismiss = (open: boolean, box: React.RefObject<HTMLElement>, close: () => void) => {
  useEffect(() => {
    if (!open) return undefined;
    const away = (event: PointerEvent) => !box.current?.contains(event.target as Node) && close();
    const escape = (event: KeyboardEvent) => event.key === "Escape" && close();
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", escape);

    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open, box, close]);
};

/**
 * A filter in the redesign's finish (`sleek.tsx`): a pill saying what it filters and what is picked, opening a compact
 * menu under it — searchable when the list is long (designers, cities). The Trend History page's `FilterSelect`, sleek.
 */
export const SleekPicker = ({
  label,
  options,
  value,
  onChange,
  mutedId,
  searchable = false,
  searchPlaceholder,
  disabled = false,
  size = "md",
}: {
  label: string;
  options: ISelectOption[];
  value: string;
  onChange: (id: string) => void;
  /** The "all" option's id — the unset filter. It is no row of the menu (nothing picked is all); the pill reads it muted,
   * and a picked value clears back to it (the × in place of the chevron). */
  mutedId?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  disabled?: boolean;
  size?: SleekSize;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const box = useRef<HTMLDivElement>(null);
  const picked = options.find((option) => String(option.id) === value);
  const choices = options.filter((option) => String(option.id) !== mutedId);
  const shown = query ? choices.filter((option) => String(option.title).toLowerCase().includes(query.toLowerCase())) : choices;
  const set = mutedId !== undefined && value !== mutedId;

  useDismiss(open, box, () => setOpen(false));

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => !was);
          setQuery("");
        }}
        className={cn(SLEEK_CONTROL, "sleek-press flex w-full items-center rounded-full disabled:opacity-50", SLEEK_FIELD[size])}
      >
        {label && <span className="flex-none text-grey">{label}</span>}
        <span className={cn("truncate font-medium", label ? "ml-auto" : "mr-auto", picked && String(picked.id) === mutedId ? "text-grey" : "text-trueBlack dark:text-white")}>
          {picked?.title ?? value}
        </span>
        <svg viewBox="0 0 16 16" className={cn("h-4 w-4 flex-none text-grey transition-transform", open && "rotate-180", set && "invisible")} aria-hidden>
          <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {set && mutedId !== undefined && (
        <button
          type="button"
          aria-label={`Clear ${label.toLowerCase()}`}
          title={`Clear ${label.toLowerCase()}`}
          onClick={() => onChange(mutedId)}
          className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-grey transition-colors hover:bg-body-200 hover:text-trueBlack dark:hover:text-white"
        >
          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
            <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" />
          </svg>
        </button>
      )}
      {open && (
        <SleekMenu
          className="w-full"
          options={shown.map((option) => ({ id: String(option.id), title: String(option.title) }))}
          value={value}
          empty={emptyOf(query)}
          onPick={(id) => {
            onChange(id);
            setOpen(false);
          }}
          {...(searchable && {
            search: (
              <input
                autoFocus
                value={query}
                placeholder={searchPlaceholder}
                onChange={(event) => setQuery(event.target.value)}
                className="mb-1 h-9 flex-none rounded-xl bg-body-150 px-3 text-[14px] text-foreground outline-none placeholder:text-grey"
              />
            ),
          })}
        />
      )}
    </div>
  );
};

/**
 * A word of a headline that is a choice (Trends Data' "SS 2027 vs RS 2027"): the word underlined, a press opening a
 * compact menu of what it could be instead — the headline's own type, the menu the filters'.
 */
export const HeadlinePicker = ({
  text,
  options,
  value,
  onChange,
  ariaLabel,
  truncate = false,
  heading,
}: {
  text: string;
  options: { id: string; title: string; hint?: string; disabled?: boolean }[];
  value: string;
  onChange: (id: string) => void;
  ariaLabel: string;
  /** In a line that must stay one (Runway Trends' title): the word gives way with "…", the chevron stays. */
  truncate?: boolean;
  /** A line over the menu's list, saying what it is in order of ("Rising · by growth vs RS 2027"). */
  heading?: string;
}) => {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  useDismiss(open, box, () => setOpen(false));

  return (
    <span ref={box} className={cn("relative inline-block", truncate && "min-w-0 max-w-full")}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className={cn("group inline-flex items-baseline gap-1.5 text-left", truncate && "max-w-full")}
        {...(truncate && { title: text })}
      >
        <span
          className={cn("underline decoration-grey/40 decoration-[0.06em] underline-offset-[0.18em] transition-[text-decoration-color] group-hover:decoration-current", truncate && "min-w-0 truncate")}
        >
          {text}
        </span>
        <svg viewBox="0 0 16 16" className={cn("h-[0.42em] w-[0.42em] flex-none self-center text-grey transition-transform", open && "rotate-180")} aria-hidden>
          <path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <SleekMenu
          className="w-64 tracking-normal"
          {...(heading && { search: <span className="px-3 pb-1 pt-1.5 text-[12px] font-normal text-grey">{heading}</span> })}
          options={options}
          value={value}
          onPick={(id) => {
            onChange(id);
            setOpen(false);
          }}
        />
      )}
    </span>
  );
};

/**
 * The headline's choice of several (Trend Insights' "from All Designers"): the underlined words say what is picked — all,
 * the one, or the first and how many more — and its menu, searchable, toggles values as checkboxes and stays open.
 */
export const HeadlineMultiPicker = ({
  allText,
  options,
  values,
  onChange,
  ariaLabel,
  searchPlaceholder,
}: {
  allText: string;
  options: { id: string; title: string }[];
  values: string[];
  onChange: (values: string[]) => void;
  ariaLabel: string;
  searchPlaceholder?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const box = useRef<HTMLSpanElement>(null);
  useDismiss(open, box, () => setOpen(false));
  // A picked value stays listed even when the other filters narrowed it away, so it can still be unpicked.
  const listed = [...values.filter((value) => !options.some((option) => option.id === value)).map((value) => ({ id: value, title: value })), ...options];
  const shown = query ? listed.filter((option) => option.title.toLowerCase().includes(query.toLowerCase())) : listed;
  const titleOf = (id: string) => listed.find((option) => option.id === id)?.title ?? id;
  const text = values.length === 0 ? allText : values.length === 1 ? titleOf(values[0] ?? "") : `${titleOf(values[0] ?? "")} +${values.length - 1}`;

  return (
    <span ref={box} className="relative inline-block">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => !was);
          setQuery("");
        }}
        className="group inline-flex items-baseline gap-1.5 text-left"
      >
        <span className="underline decoration-grey/40 decoration-[0.06em] underline-offset-[0.18em] transition-[text-decoration-color] group-hover:decoration-current">{text}</span>
        <svg viewBox="0 0 16 16" className={cn("h-[0.42em] w-[0.42em] flex-none self-center text-grey transition-transform", open && "rotate-180")} aria-hidden>
          <path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <SleekMenu
          className="w-80 text-[14px] tracking-normal"
          options={shown}
          value=""
          values={values}
          empty={emptyOf(query)}
          onPick={(id) => onChange(values.includes(id) ? values.filter((value) => value !== id) : [...values, id])}
          search={
            <span className="mb-1 flex flex-none items-center gap-2">
              <input
                autoFocus
                value={query}
                placeholder={searchPlaceholder}
                onChange={(event) => setQuery(event.target.value)}
                className="h-9 min-w-0 flex-1 rounded-xl bg-body-150 px-3 text-[14px] font-normal text-foreground outline-none placeholder:text-grey"
              />
              {values.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="h-9 flex-none rounded-xl px-3 text-[13px] font-medium text-grey hover:bg-body-150 hover:text-trueBlack dark:hover:text-white"
                >
                  {allText}
                </button>
              )}
            </span>
          }
        />
      )}
    </span>
  );
};

/**
 * A filter of several values (REF-7087: OR inside a filter, AND across): the sleek pill says what is picked — the one,
 * or the first and how many more — and its menu toggles values, staying open; the × clears them all.
 */
export const SleekMultiPicker = ({
  label,
  options,
  values,
  onChange,
  allLabel,
  searchable = false,
  searchPlaceholder,
  size = "md",
}: {
  label: string;
  options: ISelectOption[];
  values: string[];
  onChange: (values: string[]) => void;
  /** What the pill says when nothing is picked (every value). */
  allLabel: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  size?: SleekSize;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, box, () => setOpen(false));
  // A picked value stays listed even when the cascade narrowed it away, so it can still be unpicked.
  const listed = [...values.filter((value) => !options.some((option) => String(option.id) === value)).map((value) => ({ id: value, title: value })), ...options];
  const shown = query ? listed.filter((option) => String(option.title).toLowerCase().includes(query.toLowerCase())) : listed;
  const titleOf = (id: string) => String(listed.find((option) => String(option.id) === id)?.title ?? id);
  // The first picked by name, and how many more beside it: the name gives way to a narrow pill, the count does not.
  const said = values.length === 0 ? allLabel : titleOf(values[0] ?? "");
  const more = values.length > 1 ? `+${values.length - 1}` : "";

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => !was);
          setQuery("");
        }}
        className={cn(SLEEK_CONTROL, "sleek-press flex w-full items-center rounded-full", SLEEK_FIELD[size])}
      >
        <span className="flex-none text-grey">{label}</span>
        <span className={cn("ml-auto flex min-w-0 items-baseline gap-1 font-medium", values.length ? "text-trueBlack dark:text-white" : "text-grey")}>
          <span className="truncate">{said}</span>
          {more && <span className="flex-none">{more}</span>}
        </span>
        <svg viewBox="0 0 16 16" className={cn("h-4 w-4 flex-none text-grey transition-transform", open && "rotate-180", values.length > 0 && "invisible")} aria-hidden>
          <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {values.length > 0 && (
        <button
          type="button"
          aria-label={`Clear ${label.toLowerCase()}`}
          title={`Clear ${label.toLowerCase()}`}
          onClick={() => onChange([])}
          className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-grey transition-colors hover:bg-body-200 hover:text-trueBlack dark:hover:text-white"
        >
          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
            <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" />
          </svg>
        </button>
      )}
      {open && (
        <SleekMenu
          className="w-full"
          options={shown.map((option) => ({ id: String(option.id), title: String(option.title) }))}
          value=""
          values={values}
          empty={emptyOf(query)}
          onPick={(id) => onChange(values.includes(id) ? values.filter((value) => value !== id) : [...values, id])}
          {...((searchable || values.length > 0) && {
            search: (
              <div className="flex flex-none flex-col">
                {searchable && (
                  <input
                    autoFocus
                    value={query}
                    placeholder={searchPlaceholder}
                    onChange={(event) => setQuery(event.target.value)}
                    className="mb-1 h-9 flex-none rounded-xl bg-body-150 px-3 text-[14px] text-foreground outline-none placeholder:text-grey"
                  />
                )}
                {/* Several picked: what is picked, and every pick undone at once. */}
                {values.length > 0 && (
                  <div className="flex items-center justify-between px-3 pb-1 pt-1 text-[12px]">
                    <span className="text-grey">{t("trends:trend_history.filters.selected", { count: values.length })}</span>
                    <button type="button" onClick={() => onChange([])} className="font-medium text-trueBlack hover:underline dark:text-white">
                      {t("trends:trend_history.filters.clear_all")}
                    </button>
                  </div>
                )}
              </div>
            ),
          })}
        />
      )}
    </div>
  );
};
